import { useId, useState } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Alert } from '../../atoms/Alert/Alert';
import { Text } from '../../atoms/Typography/Text';
import { useTranslation } from '../../../i18n/useTranslation';
import './BetaAccessNotice.css';

export const BETA_CONTACT_EMAIL = 'dennis@spiegelreef.com';

export interface BetaAccessNoticeProps {
  /**
   * Sign-in: one line with "Mehr erfahren" — returning users do not need the whole
   * notice above the password field (usability audit §5.4). Sign-up shows it in full.
   */
  compact?: boolean;
}

/**
 * Static notice on the sign-in and sign-up pages: ReefGuide is a closed beta,
 * every new account is reviewed and activated by an admin, access can be withdrawn. Not announced
 * (live="off") — it is page content, not a reaction to a user action.
 */
export function BetaAccessNotice({ compact = false }: BetaAccessNoticeProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(!compact);
  const bodyId = useId();
  return (
    <Alert
      tone="warning"
      live="off"
      title={t('auth.betaNotice.title')}
      className="beta-access-notice"
      action={
        compact ? (
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? t('auth.betaNotice.less') : t('auth.betaNotice.more')}
          </Button>
        ) : undefined
      }
    >
      <span id={bodyId} hidden={!expanded}>
        <Text as="span" className="beta-access-notice__text">{t('auth.betaNotice.body')}</Text>{' '}
        <Text as="span" className="beta-access-notice__text">
          {t('auth.betaNotice.contact')}{' '}
          <a className="beta-access-notice__link" href={`mailto:${BETA_CONTACT_EMAIL}`}>
            {BETA_CONTACT_EMAIL}
          </a>
        </Text>
      </span>
    </Alert>
  );
}

export default BetaAccessNotice;
